/**
 * FieldValuePure
 *
 * Pure presentational component for displaying a field value with actions.
 * Works for both contact fields and org fields.
 * All data and callbacks are passed via flattened props.
 */
import { cn } from '../cn';
import Button from '../Input2/Button';
import ButtonGroup from '../Input2/ButtonGroup';
import { General, Security } from '../StrokeIcons';
import { FieldIcon } from './FieldIcon';

export type FieldValuePureProps = {
  // Field data (flattened)
  data: string | boolean;
  source: string;
  confidenceScore?: number | null;

  // Verification state (for contact fields)
  verified?: boolean | null;
  verifiedMessage?: string;

  // Whether this is the primary value
  isPrimary?: boolean;

  // Callbacks (simplified - no field name/source params since parent knows them)
  onCopied?: (data: string) => void;
  onCopyFailed?: (data: string) => void;
  onDelete?: () => void;
  onMakePrimary?: () => void;
  onRemovePrimary?: () => void;
  onVerify?: () => void;
};

export function FieldValuePure({
  data,
  source,
  confidenceScore,
  verified,
  verifiedMessage,
  isPrimary,
  onCopied,
  onCopyFailed,
  onDelete,
  onMakePrimary,
  onRemovePrimary,
  onVerify,
}: FieldValuePureProps) {
  const dataStr = typeof data === 'boolean' ? (data ? 'true' : 'false') : data;

  const handleCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    navigator.clipboard
      .writeText(dataStr)
      .then(() => onCopied?.(dataStr))
      .catch(() => onCopyFailed?.(dataStr));
  };

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    onDelete?.();
  };

  const handleMakePrimary = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    onMakePrimary?.();
  };

  const handleRemovePrimary = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    onRemovePrimary?.();
  };

  const handleVerify = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    onVerify?.();
  };

  return (
    <div
      className={cn('group/row relative flex items-center gap-1.5 text-sm text-neutral-600 dark:text-neutral-100', {
        'text-mantis-600 dark:text-mantis-400': verified === true,
        'text-fair-pink-600 dark:text-fair-pink-400': verified === false,
        'font-medium': isPrimary,
      })}>
      <FieldIcon source={source} confidenceScore={confidenceScore} />

      <p className="grow truncate">{dataStr}</p>

      {/* Action buttons hover-overlay. Absolutely positioned so they don't reserve
          horizontal space — values like "Halloran" should display in full when the column
          has room, instead of being truncated to "Hall…" by phantom button-group width. */}
      <ButtonGroup
        size="sm"
        className="absolute right-0 top-1/2 -translate-y-1/2 bg-white opacity-0 pointer-events-none transition-opacity group-hover/row:opacity-100 group-hover/row:pointer-events-auto dark:bg-slate-900">
        {onDelete && <Button icon={<General.Trash01 />} color="red" onClick={handleDelete} tooltip="Delete" />}
        <Button icon={<General.Copy01 />} onClick={handleCopy} tooltip="Copy" />

        {verified === true && (
          <Button
            className="text-mantis-600"
            onClick={(e) => e.stopPropagation()}
            icon={<Security.Shield01 />}
            tooltip="Verified & Valid"
          />
        )}
        {verified === false && (
          <Button
            className="text-fair-pink-600"
            onClick={(e) => e.stopPropagation()}
            icon={<Security.Shield01 />}
            tooltip={verifiedMessage || 'Verified but Invalid'}
          />
        )}
        {verified == null && onVerify && (
          <Button icon={<Security.Shield01 />} onClick={handleVerify} tooltip="Unverified (click to verify)" />
        )}

        {onMakePrimary && (
          <Button tooltip="Set as default" icon={<General.CheckCircle />} onClick={handleMakePrimary} />
        )}
        {onRemovePrimary && (
          <Button tooltip="Remove default" icon={<General.XMark />} onClick={handleRemovePrimary} />
        )}
      </ButtonGroup>
    </div>
  );
}
